import { useEffect, useState } from "react";
import { Container, Form, Button, Row, Col, Accordion } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

import { saveEventDetails } from "../Api/Event";
import { fetchProjectsByOrg } from "../Api/ViewProjects";
import { getTicketsByOrgId } from "../Api/TicketApiOrg";
import { getAllEventTypes } from "../Api/Event";
import { getDepartmentsByOrgId } from "../Api/DepartmentOrgMaster";
import { getAllCompanyCodes } from "../Api/CompanyLocationMaster";
import { fetchCostCenters } from "../Api/ResourceMaster";
import { getResourcesByOrgId } from "../Api/ResourceList";
import { fetchCustomersByOrgId } from "../Api/InvoiceApi";

import Sample14 from "../Components/Logo";

import {
  FaCalendarAlt,
  FaUsers,
  FaBell,
  FaClipboardList,
} from "react-icons/fa";

const Event = () => {
  const navigate = useNavigate();

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));

  const orgId = String(userDetails?.organisationId);
  const userId = String(userDetails?.userId);

  const [loading, setLoading] = useState(false);

  const [projects, setProjects] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [eventTypes, setEventTypes] = useState([]);

  const [participants, setParticipants] = useState([]);

  const [reminderMinutes, setReminderMinutes] = useState("");
  const [reminderType, setReminderType] = useState("");
  const [isSent, setIsSent] = useState(false);

  const [momDetails, setMomDetails] = useState([
    {
      discussionPoint: "",
      decisionTaken: "",
      actionItem: "",
    },
  ]);

  const [momMeta, setMomMeta] = useState({
    preparedBy: "",
    approvedBy: "",
  });

  // Dropdown data states
  const [companyOptions, setCompanyOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [costCenterOptions, setCostCenterOptions] = useState([]);
  const [resourceOptions, setResourceOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);

  const [eventData, setEventData] = useState({
    eventName: "",
    eventType: "",
    eventDate: "",
    eventEndDate: "",
    eventStartTime: "",
    eventEndTime: "",
    eventLocation: "",
    organizerName: "",
    budgetAllocation: "",
    department: "",
    eventDescription: "",
    entityType: "",
    entityId: "",
    createdBy: userId,
    organisationId: orgId,
    eventCompanyCode: "",
    eventCostCenter: "",
    eventResource: "",
    eventClient: "",
  });

  // Fetch Company Codes
  const fetchCompanyCodes = async () => {
    try {
      const data = await getAllCompanyCodes(orgId);
      const options = data.map((company) => ({
        value: company.companyCode,
        label: `${company.name} (${company.companyCode})`,
      }));
      // Sort alphabetically by label
      const sortedOptions = options.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setCompanyOptions(sortedOptions);
    } catch (error) {
      console.error("Error fetching company codes:", error);
      toast.error("Failed to load company codes");
    }
  };

  // Fetch Departments
  const fetchDepartments = async () => {
    try {
      const data = await getDepartmentsByOrgId(orgId);
      const options = data.map((dept) => ({
        value: dept.guid,
        label: `${dept.deptDescription} (${dept.deptCode})`,
      }));
      // Sort alphabetically by label
      const sortedOptions = options.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setDepartmentOptions(sortedOptions);
    } catch (error) {
      console.error("Error fetching departments:", error);
      toast.error("Failed to load departments");
    }
  };

  // Fetch Cost Centers
  const fetchCostCentersList = async () => {
    try {
      const data = await fetchCostCenters();
      const options = data.map((center) => ({
        value: center.code,
        label: `${center.description} (${center.code})`,
      }));
      // Sort alphabetically by label
      const sortedOptions = options.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setCostCenterOptions(sortedOptions);
    } catch (error) {
      console.error("Error fetching cost centers:", error);
      toast.error("Failed to load cost centers");
    }
  };

  // Fetch Resources
  const fetchResources = async () => {
    try {
      const data = await getResourcesByOrgId(orgId);
      const options = data.map((resource) => ({
        value: resource.resourceId,
        label: `${resource.name} (${resource.resourceId})`,
      }));
      // Sort alphabetically by label
      const sortedOptions = options.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setResourceOptions(sortedOptions);
    } catch (error) {
      console.error("Error fetching resources:", error);
      toast.error("Failed to load resources");
    }
  };

  // Fetch Customers
  const fetchCustomers = async () => {
    try {
      const data = await fetchCustomersByOrgId(orgId);
      const options = data.map((customer) => ({
        value: customer.customerCode,
        label: `${customer.name} (${customer.customerCode})`,
        ...customer,
      }));
      // Sort alphabetically by label
      const sortedOptions = options.sort((a, b) =>
        a.label.localeCompare(b.label),
      );
      setCustomerOptions(sortedOptions);
      setCustomers(data);
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to load customers");
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;

    setEventData({
      ...eventData,
      [name]: value,
    });

    try {
      if (name === "entityType" && value === "Project") {
        const data = await fetchProjectsByOrg(orgId);
        // Sort projects alphabetically by title
        const sortedProjects = data.sort((a, b) =>
          a.prTitle.localeCompare(b.prTitle),
        );
        setProjects(sortedProjects);
      }

      if (name === "entityType" && value === "Ticket") {
        const data = await getTicketsByOrgId(orgId);
        // Sort tickets alphabetically by name
        const sortedTickets = data.sort((a, b) =>
          a.ticketName.localeCompare(b.ticketName),
        );
        setTickets(sortedTickets);
      }
    } catch {
      toast.error("Error loading entity data");
    }
  };

  const projectOptions = projects.map((p) => ({
    value: p.projectId,
    label: `${p.prTitle} (${p.projectId})`,
  }));

  const ticketOptions = tickets.map((t) => ({
    value: t.ticketId,
    label: `${t.ticketName} (${t.ticketId})`,
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const payload = {
        ...eventData,

        eventDate: new Date(eventData.eventDate).toISOString(),

        budgetAllocation: eventData.budgetAllocation
          ? Number(eventData.budgetAllocation)
          : null,

        eventEndDate: eventData.eventEndDate
          ? new Date(eventData.eventEndDate).toISOString()
          : null,

        eventStartTime: eventData.eventStartTime
          ? `${eventData.eventStartTime}:00`
          : null,

        eventEndTime: eventData.eventEndTime
          ? `${eventData.eventEndTime}:00`
          : null,

        eventCompanyCode: eventData.eventCompanyCode || null,
        eventCostCenter: eventData.eventCostCenter || null,
        eventResource: eventData.eventResource || null,
        eventClient: eventData.eventClient || null,

        participants: participants.map((p) => ({
          userId: p.userId,
          participantRole: p.participantRole,
          status: p.status,
          createdBy: userId,
          organisationId: orgId,
        })),

        reminders: reminderMinutes
          ? [
              {
                reminderMinutes: Number(reminderMinutes),
                reminderType,
                isSent,
                createdBy: userId,
                organisationId: orgId,
              },
            ]
          : [],

        minutesOfMeeting: {
          preparedBy: momMeta.preparedBy,
          approvedBy: momMeta.approvedBy,

          discussionPoints: momDetails.map((x) => x.discussionPoint),
          decisionsTaken: momDetails.map((x) => x.decisionTaken),
          actionItems: momDetails.map((x) => x.actionItem),

          createdBy: userId,
          organisationId: orgId,
        },
      };

      await saveEventDetails(payload);

      toast.success("Event Created Successfully");

      navigate("/viewEvent");
    } catch {
      toast.error("Error saving event");
    } finally {
      setLoading(false);
    }
  };

  const eventTypeOptions = eventTypes
    .map((e) => ({
      value: e.eventTypeGuid,
      label: e.typeName,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)); // Sort event types alphabetically

  useEffect(() => {
    fetchEventTypes();
    fetchCompanyCodes();
    fetchDepartments();
    fetchCostCentersList();
    fetchResources();
    fetchCustomers();
  }, []);

  const fetchEventTypes = async () => {
    try {
      const data = await getAllEventTypes();
      // Sort event types alphabetically by typeName
      const sortedData = data.sort((a, b) =>
        a.typeName.localeCompare(b.typeName),
      );
      setEventTypes(sortedData);
    } catch (err) {
      toast.error("Failed to load event types");
    }
  };

  return (
    <div
      className="container resource-master-fields"
      style={{ marginTop: "30px" }}
    >
      <h2 className="fields-title">
        <span style={{ fontSize: "2rem", fontWeight: 700 }}>Event Master</span>
      </h2>

      {loading && (
        <div className="loading-overlay">
          <Sample14 />
        </div>
      )}

      <Form onSubmit={handleSubmit} className="fields-form">
        <Accordion defaultActiveKey="0" alwaysOpen>
          {/* EVENT DETAILS */}

          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <div className="details-header-content">
                <FaCalendarAlt
                  style={{ fontSize: "15px", marginLeft: "10px" }}
                />
                <span style={{ fontSize: "15px", margin: "7px" }}>
                  Event Details
                </span>
              </div>
            </Accordion.Header>

            <Accordion.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Event Name</Form.Label>
                    <Form.Control
                      name="eventName"
                      value={eventData.eventName}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Event Type</Form.Label>
                    <Select
                      options={eventTypeOptions}
                      value={eventTypeOptions.find(
                        (opt) => opt.value === eventData.eventType,
                      )}
                      onChange={(selected) =>
                        setEventData({
                          ...eventData,
                          eventType: selected?.value || "",
                        })
                      }
                      placeholder="Select Event Type"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Event Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="eventDate"
                      value={eventData.eventDate}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="eventEndDate"
                      value={eventData.eventEndDate}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company Code</Form.Label>
                    <Select
                      options={companyOptions}
                      value={companyOptions.find(
                        (opt) => opt.value === eventData.eventCompanyCode,
                      )}
                      onChange={(selected) =>
                        setEventData({
                          ...eventData,
                          eventCompanyCode: selected?.value || "",
                        })
                      }
                      placeholder="Select Company"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cost Center</Form.Label>
                    <Select
                      options={costCenterOptions}
                      value={costCenterOptions.find(
                        (opt) => opt.value === eventData.eventCostCenter,
                      )}
                      onChange={(selected) =>
                        setEventData({
                          ...eventData,
                          eventCostCenter: selected?.value || "",
                        })
                      }
                      placeholder="Select Cost Center"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Resource</Form.Label>
                    <Select
                      options={resourceOptions}
                      value={resourceOptions.find(
                        (opt) => opt.value === eventData.eventResource,
                      )}
                      onChange={(selected) =>
                        setEventData({
                          ...eventData,
                          eventResource: selected?.value || "",
                        })
                      }
                      placeholder="Select Resource"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* ROW FOR EVENT CLIENT */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Event Client</Form.Label>
                    <Select
                      options={customerOptions}
                      value={customerOptions.find(
                        (opt) => opt.value === eventData.eventClient,
                      )}
                      onChange={(selected) =>
                        setEventData({
                          ...eventData,
                          eventClient: selected?.value || "",
                        })
                      }
                      placeholder="Select Client"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <Form.Control
                      name="eventLocation"
                      value={eventData.eventLocation}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Organizer</Form.Label>
                    <Form.Control
                      name="organizerName"
                      value={eventData.organizerName}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Start Time</Form.Label>
                    <Form.Control
                      type="time"
                      name="eventStartTime"
                      value={eventData.eventStartTime}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>End Time</Form.Label>
                    <Form.Control
                      type="time"
                      name="eventEndTime"
                      value={eventData.eventEndTime}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Budget</Form.Label>
                    <Form.Control
                      type="number"
                      name="budgetAllocation"
                      value={eventData.budgetAllocation}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Department</Form.Label>
                    <Select
                      options={departmentOptions}
                      value={departmentOptions.find(
                        (opt) => opt.value === eventData.department,
                      )}
                      onChange={(selected) =>
                        setEventData({
                          ...eventData,
                          department: selected?.value || "",
                        })
                      }
                      placeholder="Select Department"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="eventDescription"
                      value={eventData.eventDescription}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Entity Type</Form.Label>
                    <Form.Select
                      name="entityType"
                      value={eventData.entityType}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      <option value="Ticket">Ticket</option>
                      <option value="Project">Project</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Entity Id</Form.Label>

                    {eventData.entityType === "Project" ? (
                      <Select
                        options={projectOptions}
                        onChange={(selected) =>
                          setEventData({
                            ...eventData,
                            entityId: selected?.value || "",
                          })
                        }
                        placeholder="Select Project"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    ) : eventData.entityType === "Ticket" ? (
                      <Select
                        options={ticketOptions}
                        onChange={(selected) =>
                          setEventData({
                            ...eventData,
                            entityId: selected?.value || "",
                          })
                        }
                        placeholder="Select Ticket"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    ) : (
                      <Form.Control
                        name="entityId"
                        value={eventData.entityId}
                        onChange={handleChange}
                        placeholder="Enter Entity Id"
                      />
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          {/* PARTICIPANTS */}
          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <div className="details-header-content">
                <FaUsers style={{ fontSize: "15px", marginLeft: "10px" }} />
                <span style={{ fontSize: "15px", margin: "7px" }}>
                  Participants
                </span>
              </div>
            </Accordion.Header>

            <Accordion.Body>
              {participants.map((p, i) => (
                <Row key={i} className="mb-3">
                  <Col md={4}>
                    <Form.Control
                      placeholder="User Id"
                      value={p.userId}
                      onChange={(e) => {
                        const arr = [...participants];
                        arr[i].userId = e.target.value;
                        setParticipants(arr);
                      }}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Control
                      placeholder="Role"
                      value={p.participantRole}
                      onChange={(e) => {
                        const arr = [...participants];
                        arr[i].participantRole = e.target.value;
                        setParticipants(arr);
                      }}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Select
                      value={p.status}
                      onChange={(e) => {
                        const arr = [...participants];
                        arr[i].status = e.target.value;
                        setParticipants(arr);
                      }}
                    >
                      <option value="">Status</option>
                      <option value="Accepted">Accepted</option>
                      <option value="Pending">Pending</option>
                      <option value="Declined">Declined</option>
                    </Form.Select>
                  </Col>
                </Row>
              ))}

              <Button
                variant="secondary"
                onClick={() =>
                  setParticipants([
                    ...participants,
                    { userId: "", participantRole: "", status: "" },
                  ])
                }
              >
                Add Participant
              </Button>
            </Accordion.Body>
          </Accordion.Item>

          {/* REMINDER */}
          <Accordion.Item eventKey="2">
            <Accordion.Header style={{ fontSize: "18px", margin: "11px" }}>
              <FaBell style={{ fontSize: "15px", marginLeft: "10px" }} />
              <span style={{ fontSize: "15px", margin: "7px" }}>
                {" "}
                Reminder{" "}
              </span>
            </Accordion.Header>

            <Accordion.Body>
              <Row>
                <Col md={4}>
                  <Form.Control
                    type="number"
                    placeholder="Minutes"
                    value={reminderMinutes}
                    onChange={(e) => setReminderMinutes(e.target.value)}
                  />
                </Col>

                <Col md={4}>
                  <Form.Select
                    value={reminderType}
                    onChange={(e) => setReminderType(e.target.value)}
                  >
                    <option value="">Type</option>
                    <option value="Email">Email</option>
                    <option value="Notification">Notification</option>
                    <option value="SMS">SMS</option>
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Select
                    value={isSent}
                    onChange={(e) => setIsSent(e.target.value === "true")}
                  >
                    <option value="">Is Sent</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Form.Select>
                </Col>
              </Row>
            </Accordion.Body>
          </Accordion.Item>

          {/* MOM */}
          <Accordion.Item eventKey="3">
            <Accordion.Header style={{ fontSize: "18px", margin: "11px" }}>
              <FaClipboardList
                style={{ fontSize: "15px", marginLeft: "10px" }}
              />
              <span style={{ fontSize: "15px", margin: "7px" }}>
                {" "}
                MoM (Minutes of Meeting){" "}
              </span>
            </Accordion.Header>

            <Accordion.Body>
              <Row>
                <Col md={6}>
                  <Form.Control
                    placeholder="Prepared By"
                    value={momMeta.preparedBy}
                    onChange={(e) =>
                      setMomMeta({ ...momMeta, preparedBy: e.target.value })
                    }
                  />
                </Col>

                <Col md={6}>
                  <Form.Control
                    placeholder="Approved By"
                    value={momMeta.approvedBy}
                    onChange={(e) =>
                      setMomMeta({ ...momMeta, approvedBy: e.target.value })
                    }
                  />
                </Col>
              </Row>

              {momDetails.map((item, i) => (
                <Row key={i} className="mb-3">
                  <Col md={4}>
                    <Form.Control
                      placeholder="Discussion"
                      value={item.discussionPoint}
                      onChange={(e) => {
                        const arr = [...momDetails];
                        arr[i].discussionPoint = e.target.value;
                        setMomDetails(arr);
                      }}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Control
                      placeholder="Decision"
                      value={item.decisionTaken}
                      onChange={(e) => {
                        const arr = [...momDetails];
                        arr[i].decisionTaken = e.target.value;
                        setMomDetails(arr);
                      }}
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Control
                      placeholder="Action Item"
                      value={item.actionItem}
                      onChange={(e) => {
                        const arr = [...momDetails];
                        arr[i].actionItem = e.target.value;
                        setMomDetails(arr);
                      }}
                    />
                  </Col>
                </Row>
              ))}

              <Button
                variant="secondary"
                onClick={() =>
                  setMomDetails([
                    ...momDetails,
                    { discussionPoint: "", decisionTaken: "", actionItem: "" },
                  ])
                }
              >
                Add Row
              </Button>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <div className="fields-btn-container" style={{ marginTop: 20 }}>
          <Button
            variant="primary"
            type="submit"
            disabled={loading}
            style={{ padding: "15px 35px", fontSize: "15px" }}
          >
            {loading ? "Saving..." : "Save Event"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default Event;
